import { useFormContext, Controller } from 'react-hook-form';
import Spacing from 'components/Spacing';
import CompanySelection from './CompanySelection';
import NonVctFields from 'routes/Pages/Company/CompanyForm/NonVctFields';
import { TextField } from '@mui/material';
import { FieldRow } from 'components/FormElements';
import StageAndSector from 'routes/Pages/Company/CompanyForm/StageAndSector';
import { investee as Investee } from 'further-ui/validations';
import { makeStyles } from 'tss-react/mui';
import { UploadType } from 'further-types/files';
import useFileUpload from 'hooks/ui/useFileUpload';
import { UploadedFile } from 'components/Dropzone';
import { useEffect } from 'react';

const useStyles = makeStyles()((theme) => ({
  errorCk: {
    color: '#f44336',
    marginTop: '4px',
    fontSize: theme.fontSizes?.sm,
  },
  warningCk: {
    marginTop: '4px',
    fontSize: theme.fontSizes?.sm,
  },
}));

type CompanyDetailsFormProps = {
  duplicateCompanyWarning?: string | null;
};

const CompanyDetailsForm: React.FC<CompanyDetailsFormProps> = ({
  duplicateCompanyWarning,
}) => {
  const { classes } = useStyles();
  const { uploadFile } = useFileUpload();

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<
    | (Investee.NewCompanyFormValues & { storedInvesteeId?: string })
    | (Investee.NewShareClassFormValues & { storedInvesteeId?: string })
  >();

  const values = watch();

  useEffect(() => {
    if (!values.isNewCompany && values.storedInvesteeId) {
      setValue('investeeId', values.storedInvesteeId);
    }
  }, [values.isNewCompany]);

  // Create a setValues wrapper that works with the existing local state pattern
  const setValues = (
    newValues: Partial<
      Investee.NewCompanyFormValues | Investee.NewShareClassFormValues
    >,
  ) => {
    Object.entries(newValues).forEach(([key, value]) => {
      setValue(
        key as keyof (
          | Investee.NewCompanyFormValues
          | Investee.NewShareClassFormValues
        ),
        value,
      );
    });
  };

  const handleChangeLogo = async (file: Array<UploadedFile>) => {
    if (file?.[0]?.path) {
      const { data } = await uploadFile(file[0], UploadType.CompanyLogos);

      setValues({
        ...values,
        companyLogo: data?.filePath,
        previewCompanyLogo: file[0]?.preview,
      });
    }
  };

  if (!values.firmId) {
    return null;
  }

  return (
    <Spacing>
      <>
        <CompanySelection
          isNewCompany={values.isNewCompany}
          control={control}
          resetCompanyDropdown={() => {
            setValue('investeeId', '');
            if (values.investeeId) {
              setValue('storedInvesteeId', values.investeeId);
            }
          }}
          onCompanyChange={(investeeId) => {
            setValue('investeeId', investeeId);
          }}
          firmId={values.firmId}
        />

        {values.isNewCompany && (
          <>
            <NonVctFields
              values={values}
              setValues={setValues}
              errorText={{
                legalName: errors.legalName?.message,
                tradingName: errors.tradingName?.message,
                description: errors.description?.message,
                companiesHouseNumber: errors.companiesHouseNumber?.message,
              }}
              classes={classes}
              duplicateCompanyWarning={duplicateCompanyWarning}
              handleChangeLogo={handleChangeLogo}
            />
            <StageAndSector
              values={values}
              setValues={setValues}
              errorText={{
                stage: errors.stage?.message,
                sector: errors.sector?.message,
              }}
            />
            <FieldRow title="Share class*" centerTitle>
              <Controller
                name="shareClass"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    placeholder="Enter share class. e.g. Ordinary"
                    required
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </FieldRow>
            <FieldRow title="Nominal share value" centerTitle>
              <Controller
                name="nominalShareValue"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    placeholder="Enter nominal share value"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </FieldRow>
          </>
        )}
      </>
    </Spacing>
  );
};

export default CompanyDetailsForm;
